export function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    image.src = url;
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('JPEG encode failed'))),
      'image/jpeg',
      quality,
    );
  });
}

export type JpegCompressionOptions = {
  /** Size the encoder aims for. */
  targetBytes?: number;
  /** Longest edge in pixels before quality is reduced. */
  maxEdge?: number;
};

/** Damage and inspection photos are evidence: keep 4K detail, at most about 3MB per photo. */
const PHOTO_TARGET_BYTES = 3_000_000;
const PHOTO_MAX_EDGE = 3840;

/**
 * Encodes an already-decoded image (an <img>, or a canvas straight from the camera) as JPEG,
 * shrinking quality and then size until it fits the target.
 */
export async function encodeJpegWithinBudget(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  { targetBytes = PHOTO_TARGET_BYTES, maxEdge = PHOTO_MAX_EDGE }: JpegCompressionOptions = {},
): Promise<File> {
  let width = sourceWidth;
  let height = sourceHeight;
  if (Math.max(width, height) > maxEdge) {
    const scale = maxEdge / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas context');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  let quality = 0.85;
  let blob: Blob | null = null;
  for (let attempt = 0; attempt < 6; attempt++) {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(source, 0, 0, width, height);
    blob = await canvasToJpeg(canvas, quality);
    if (blob.size <= targetBytes || (width <= 640 && quality <= 0.45)) break;
    if (blob.size > targetBytes * 1.4) {
      width = Math.round(width * 0.8);
      height = Math.round(height * 0.8);
    } else {
      quality = Math.max(0.4, quality - 0.15);
    }
  }

  return new File([blob!], 'photo.jpg', { type: 'image/jpeg' });
}

/** Compress toward the target size. Keeps the original if it is already a small JPEG. */
export async function compressImageToJpeg(
  input: Blob,
  options: JpegCompressionOptions = {},
): Promise<File> {
  if (
    input.type === 'image/jpeg' &&
    input.size <= (options.targetBytes ?? PHOTO_TARGET_BYTES) * 1.1
  ) {
    return input instanceof File ? input : new File([input], 'photo.jpg', { type: 'image/jpeg' });
  }

  const image = await loadImage(input);
  return encodeJpegWithinBudget(
    image,
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    options,
  );
}
